/**
 * /login — refonte « bonnes affaires » du 2026-09-14.
 *
 * Coque commune (en-tête du site, bandeau court) au lieu d'une page isolée au
 * logo géant. Même logique : mot de passe, puis code 2FA si le compte l'exige
 * (cookie vx_2fa_pending, 5 minutes). Après connexion, on revient sur la page
 * demandée (?suivant=, posé par la fiche et les cartes) ; à défaut, sur les
 * bonnes affaires, ou l'espace garage pour un garage.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { login, verify2fa } from '../services/auth';
import { useAuth } from '../context/AuthContext';
import Page from '../components/Layout/Page';
import BandeauAffaires from '../components/Affaires/BandeauAffaires';
import './AuthPages.css';

// Durée de vie du cookie vx_2fa_pending posé par le backend (300 s).
const PENDING_TTL_SEC = 300;

/* Page où revenir après connexion ou inscription : un chemin INTERNE
   seulement (« /listings/123 »). Un « //site.tld » ou une URL complète
   ferait de ce paramètre une redirection ouverte. */
export function pageSuivante(searchParams) {
  const v = searchParams.get('suivant') || '';
  return /^\/(?![/\\])/.test(v) ? v : null;
}

export function destinationApresConnexion(profileType) {
  if (profileType === 'garage') return '/garage';
  return '/explore';
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [step, setStep] = useState('credentials');
  const [code, setCode] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(PENDING_TTL_SEC);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const suivant = pageSuivante(searchParams);

  // Au-delà de 5 min le backend rejette le code : on ramène l'utilisateur à
  // l'écran mot de passe avant qu'il ne saisisse un code voué à échouer.
  useEffect(() => {
    if (step !== '2fa') return undefined;
    if (secondsLeft <= 0) {
      backToCredentials('Session expirée (5 min). Reconnectez-vous.');
      return undefined;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [step, secondsLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  function backToCredentials(msg) {
    setStep('credentials');
    setCode('');
    setPassword('');
    setSecondsLeft(PENDING_TTL_SEC);
    setError(msg || '');
  }

  function finishLogin(data) {
    signIn(data.user);
    navigate(suivant || destinationApresConnexion(data.user?.profile_type));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.requires_2fa) {
        setStep('2fa');
        setCode('');
        setSecondsLeft(PENDING_TTL_SEC);
        return;
      }
      finishLogin(data);
    } catch (err) {
      if (!err.response) setError('Serveur inaccessible. Veuillez réessayer dans quelques instants.');
      else if (err.response.status === 401) setError('Email ou mot de passe incorrect.');
      else if (err.response.status === 429) setError('Trop de tentatives. Réessayez dans 1 minute.');
      else setError(err.response?.data?.detail || 'Une erreur est survenue. Réessayez.');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify2fa(e) {
    e.preventDefault();
    if (code.length !== 6) { setError('Le code doit contenir 6 chiffres.'); return; }
    setError('');
    setLoading(true);
    try {
      finishLogin(await verify2fa(code));
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (!err.response) setError('Serveur inaccessible. Veuillez réessayer dans quelques instants.');
      else if (detail.includes('expirée') || detail.includes('invalide')) backToCredentials('Session 2FA expirée. Reconnectez-vous.');
      else { setError(detail || 'Code incorrect. Réessayez.'); setCode(''); }
    } finally {
      setLoading(false);
    }
  }

  const deuxFacteurs = step === '2fa';

  return (
    <Page
      largeur="etroite"
      titre={deuxFacteurs ? 'Vérification' : 'Connexion'}
      chapeau={deuxFacteurs
        ? 'Saisissez le code à 6 chiffres de votre application d’authentification.'
        : 'Retrouvez vos favoris, vos alertes et le lien de chaque annonce.'}
    >
      <div className="zk-carte zk-auth-carte">
        {deuxFacteurs ? (
          <form onSubmit={handleVerify2fa}>
            <div className="zk-champ">
              <label htmlFor="totp-code">Code de vérification</label>
              <input
                id="totp-code" className="zk-auth-code" type="text" inputMode="numeric" autoComplete="one-time-code"
                pattern="[0-9]*" maxLength={6} autoFocus required placeholder="000000"
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
              <small>Code valable {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}</small>
            </div>
            {error && <div role="alert" className="zk-alerte erreur" style={{ marginBottom: 14 }}>{error}</div>}
            <button type="submit" className="zk-bouton plein" disabled={loading || code.length !== 6}>
              {loading ? 'Vérification…' : 'Vérifier'}
            </button>
            <button type="button" className="zk-lien" style={{ marginTop: 14 }} onClick={() => backToCredentials()}>
              ← Revenir à la connexion
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="zk-champ">
              <label htmlFor="login-email">Adresse email</label>
              <input id="login-email" type="email" autoComplete="email" required placeholder="vous@email.com"
                value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="zk-champ">
              <label htmlFor="login-mdp">Mot de passe</label>
              <div className="zk-auth-mdp">
                <input id="login-mdp" type={showPass ? 'text' : 'password'} autoComplete="current-password" required
                  placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
                <button type="button" className="zk-auth-voir" onClick={() => setShowPass((v) => !v)}
                  aria-label={showPass ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}>
                  {showPass ? 'Masquer' : 'Afficher'}
                </button>
              </div>
            </div>
            {error && <div role="alert" className="zk-alerte erreur" style={{ marginBottom: 14 }}>{error}</div>}
            <button type="submit" className="zk-bouton plein" disabled={loading}>
              {loading ? 'Connexion…' : 'Se connecter'}
            </button>
          </form>
        )}

        <div className="zk-auth-liens">
          <Link to="/forgot-password" className="zk-lien">Mot de passe oublié ?</Link>
          <Link to={suivant ? `/register?suivant=${encodeURIComponent(suivant)}` : '/register'} className="zk-lien">Créer un compte gratuit →</Link>
        </div>
      </div>

      <BandeauAffaires />
    </Page>
  );
}
